import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Text, Box } from 'rebass/styled-components';
import { useTheme } from 'styled-components';
import { RefreshCw, Zap, AlertTriangle, Pause } from 'lucide-react';

// Single source of truth for status appearance and copy
// Colors reference rombo theme tokens where available
const STATUS_CONFIG = {
  not_completed: {
    label: 'Not connected',
    bgColor: 'neutral.20',
  },
  not_shared: {
    label: 'Not connected',
    bgColor: 'neutral.20',
  },
  syncing: {
    label: 'Syncing',
    bgColor: 'blue.50',
    iconColor: 'blue.600',
    Icon: RefreshCw,
  },
  active: {
    label: 'Active',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  in_sync: {
    label: 'Active',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  permissions_missing: {
    label: 'Permissions missing',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  paused: {
    label: 'Paused',
    bgColor: 'neutral.20',
    iconColor: 'neutral.400',
    Icon: Pause,
  },
  reconnection_needed: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  no_recent_sync: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  not_logged_in_to_linkedin: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
};

export const IntegrationStatus = ({ status }) => {
  const { colors } = useTheme();
  const { label, bgColor, iconColor, Icon } = STATUS_CONFIG[status] || STATUS_CONFIG.not_completed;

  // Resolve icon color from theme (e.g., 'green.600' -> '#24A84E')
  const [colorKey, shade] = iconColor ? iconColor.split('.') : [];
  const resolvedIconColor = colors[colorKey]?.[shade] || iconColor;

  return (
    <Flex
      as="span"
      bg={bgColor}
      color="text.dark"
      sx={{
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        px: '12px',
        py: '0px',
        borderRadius: '99px',
      }}
    >
      {Icon && (
        <Box
          as={Icon}
          width="16px"
          height="16px"
          color={resolvedIconColor}
          aria-hidden="true"
          sx={
            status === 'syncing'
              ? {
                  '@keyframes spinCcw': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                  animation: 'spinCcw 3s linear infinite',
                }
              : undefined
          }
        />
      )}
      <Text as="span" fontSize="14px" fontWeight="500" sx={{ lineHeight: '24px' }}>
        {label}
      </Text>
    </Flex>
  );
};

IntegrationStatus.propTypes = {
  status: PropTypes.oneOf([
    'syncing',
    'in_sync',
    'active',
    'permissions_missing',
    'paused',
    'reconnection_needed',
    'no_recent_sync',
    'not_logged_in_to_linkedin',
    'not_completed',
    'not_shared',
  ]).isRequired,
};

export default IntegrationStatus;
